import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSettings } from '../stores/settings.js';
import { haptic } from '../lib/telegram.js';

interface SideMenuProps {
  open: boolean;
  onClose: () => void;
}

interface MenuItem {
  label: string;
  caption?: string;
  href: string;
  external?: boolean;
  icon: React.ReactNode;
}

export function SideMenu({ open, onClose }: SideMenuProps) {
  const soundsOn = useSettings((s) => s.soundsOn);
  const toggleSounds = useSettings((s) => s.toggleSounds);
  const items: MenuItem[] = [
    {
      label: 'Правила игры',
      caption: 'Подкидной дурак — кратко',
      href: '/rules',
      icon: <IconBook />,
    },
    {
      label: 'Баланс и Stars',
      caption: 'Как пополнять и возвращать',
      href: '/balance',
      icon: <IconWallet />,
    },
    {
      label: 'Условия',
      caption: 'Соглашение пользователя',
      href: '/terms',
      icon: <IconShield />,
    },
    {
      label: 'Конфиденциальность',
      caption: 'Какие данные мы храним',
      href: '/privacy',
      icon: <IconLock />,
    },
    {
      label: 'Поддержка',
      caption: '@durakapp_game_bot',
      href: 'https://t.me/durakapp_game_bot',
      external: true,
      icon: <IconHelp />,
    },
    {
      label: 'Поделиться',
      caption: 'Скинуть друзьям ссылку на бот',
      href:
        'https://t.me/share/url?url=' +
        encodeURIComponent('https://t.me/durakapp_game_bot/play') +
        '&text=' +
        encodeURIComponent('Сыграем в подкидного на Stars?'),
      external: true,
      icon: <IconShare />,
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0, 0, 0, 0.55)' }}
          />
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="fixed inset-y-0 left-0 z-50 flex w-[82%] max-w-[320px] flex-col gap-2 px-4 pb-6"
            style={{
              background:
                'linear-gradient(180deg, color-mix(in oklab, var(--color-felt-deep) 96%, white 4%) 0%, var(--color-felt-deep) 100%)',
              borderRight: '1px solid color-mix(in oklab, var(--color-gold) 35%, transparent)',
              boxShadow: '20px 0 40px -20px rgba(0,0,0,0.7)',
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
            }}
          >
            <div className="flex items-center justify-between pb-2">
              <h2 className="heading-display text-2xl">Меню</h2>
              <button
                onClick={onClose}
                aria-label="Закрыть"
                className="grid h-9 w-9 place-items-center rounded-full"
                style={{
                  background: 'var(--color-glass)',
                  border: '1px solid color-mix(in oklab, var(--color-gold) 30%, transparent)',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" className="text-gold">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
            </div>

            <ul className="flex flex-col gap-2">
              {items.map((item) => {
                const content = (
                  <span className="flex items-center gap-3">
                    <span
                      className="grid h-9 w-9 place-items-center rounded-full"
                      style={{
                        background: 'color-mix(in oklab, var(--color-gold) 16%, transparent)',
                        color: 'var(--color-gold)',
                      }}
                    >
                      {item.icon}
                    </span>
                    <span className="flex flex-col">
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: 16 }}>
                        {item.label}
                      </span>
                      {item.caption && (
                        <span className="text-[11px] text-ivory/55">{item.caption}</span>
                      )}
                    </span>
                  </span>
                );
                const className =
                  'flex items-center gap-2 rounded-2xl px-3 py-2.5 transition-colors hover:bg-white/[0.04]';
                const style = {
                  background: 'var(--color-glass)',
                  border: '1px solid color-mix(in oklab, var(--color-gold) 22%, transparent)',
                };
                return (
                  <li key={item.label}>
                    {item.external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noreferrer"
                        onClick={onClose}
                        className={className}
                        style={style}
                      >
                        {content}
                      </a>
                    ) : (
                      <Link to={item.href} onClick={onClose} className={className} style={style}>
                        {content}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>

            <button
              onClick={() => {
                haptic('light');
                toggleSounds();
              }}
              className="flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5 mt-2"
              style={{
                background: 'var(--color-glass)',
                border: '1px solid color-mix(in oklab, var(--color-gold) 22%, transparent)',
              }}
            >
              <span className="flex items-center gap-3">
                <span
                  className="grid h-9 w-9 place-items-center rounded-full"
                  style={{
                    background: 'color-mix(in oklab, var(--color-gold) 16%, transparent)',
                    color: 'var(--color-gold)',
                  }}
                >
                  <IconSound on={soundsOn} />
                </span>
                <span className="flex flex-col text-left">
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 16 }}>Звуки</span>
                  <span className="text-[11px] text-ivory/55">{soundsOn ? 'включены' : 'выключены'}</span>
                </span>
              </span>
              <span
                className="grid h-6 w-11 items-center rounded-full px-0.5"
                style={{
                  background: soundsOn ? 'var(--color-gold)' : 'var(--color-glass)',
                  border: '1px solid color-mix(in oklab, var(--color-gold) 40%, transparent)',
                  justifyContent: soundsOn ? 'end' : 'start',
                  display: 'flex',
                }}
              >
                <span
                  className="h-5 w-5 rounded-full"
                  style={{ background: soundsOn ? 'var(--color-felt-deep)' : 'var(--color-ivory)' }}
                />
              </span>
            </button>

            <div className="mt-auto flex flex-col items-center gap-1 pt-4 text-[11px] text-ivory/45">
              <span style={{ fontFamily: 'var(--font-display)' }}>durakapp · v0.1</span>
              <span>© 2026 nexaweb</span>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function IconWallet() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="6" width="18" height="13" rx="2.5" />
      <path d="M3 9h13a3 3 0 0 1 3 3v1a3 3 0 0 1-3 3H3" />
      <circle cx="16" cy="12.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconBook() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h11a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3z" />
      <path d="M4 17a3 3 0 0 1 3-3h11" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 4 6v6c0 5 4 8 8 9 4-1 8-4 8-9V6z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function IconHelp() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7" />
      <line x1="12" y1="17" x2="12" y2="17.01" />
    </svg>
  );
}

function IconSound({ on }: { on: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 9h3l4-3v12l-4-3H5z" />
      {on ? (
        <>
          <path d="M16 8.5a4.5 4.5 0 0 1 0 7" />
          <path d="M19 6a8 8 0 0 1 0 12" />
        </>
      ) : (
        <>
          <line x1="16" y1="8" x2="22" y2="14" />
          <line x1="22" y1="8" x2="16" y2="14" />
        </>
      )}
    </svg>
  );
}

function IconShare() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <line x1="8.2" y1="11" x2="15.8" y2="7" />
      <line x1="8.2" y1="13" x2="15.8" y2="17" />
    </svg>
  );
}
